/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { BrandingUiInfoKeys } from '../../../core/entities';
import { DefaultServicesClientImpl } from '../../../core/impls';
import { Locale, LocaleKeys } from '../../../core/locale';
import { ScreenController } from '../../../core/navigation/ScreenController';
import type { TivoPayloadAction } from '../../../core/tivo/tivo-redux-wrapper';
import { ListTypes } from '../../../core/utils';
import {
    onKeyDownList,
    onKeyUpList,
    onMouseOutList,
    onMouseOverList,
} from '../../list/list.reducers';
import {
    showCloudDvrHelpScreen,
    showGuideScreen,
    showMainSettingsScreen,
    showPCHelpScreen,
} from '../../screens/screens.reducers';
import { setHelpListAction, setSelectedHelpOptionAction } from './help.actions';
import { SET_HELP_OPTIONS_LIST, SET_HELP_SELECTED_OPTION } from './help.types';
import { IHelpSelectedOption } from './IHelpSelectedOption';

interface IHelp {
    optionsList: Array<IHelpSelectedOption>;
    selectedOption?: IHelpSelectedOption;
}

const initialState: IHelp = {
    optionsList: [],
    selectedOption: undefined,
};

const helpReducer = (state = initialState, { type, payload } = <TivoPayloadAction<IHelp>>{}) => {
    switch (type) {
        case SET_HELP_OPTIONS_LIST:
            return {
                ...state,
                optionsList: payload.optionsList,
                selectedOption: state.selectedOption ?? payload.optionsList[0],
            };
        case SET_HELP_SELECTED_OPTION:
            return {
                ...state,
                selectedOption: payload.selectedOption,
            };
        default:
            return state;
    }
};

export const setHelpOptionsList = () => (dispatch: Function) => {
    const displayName = new DefaultServicesClientImpl().getBrandingUiInfoForKey(
        BrandingUiInfoKeys.DISPLAY_NAME,
    );
    const settingsItemList: Array<IHelpSelectedOption> = [
        {
            value: Locale.getString(LocaleKeys.SETTINGS_PC_PARENTAL_CONTROLS_TEXT),
            preview: Locale.getString(LocaleKeys.SETTINGS_HELP_PC_PREVIEW_TEXT).replace(
                'DISPLAY_NAME',
                displayName,
            ),
        },
        {
            value: Locale.getString(LocaleKeys.CLOUD_DVR_SERVICE_TITLE),
            preview: Locale.getString(LocaleKeys.SETTINGS_HELP_DVR_PREVIEW_TEXT).replace(
                'DISPLAY_NAME',
                displayName,
            ),
        },
    ];
    dispatch(setHelpListAction(settingsItemList));
};

export const resetHelpOptionsList = () => (dispatch: Function) => {
    dispatch(setHelpListAction([]));
};

/**
 * Below reducers are related to operations on key events.
 */

export const onKeyBackHelp = () => (dispatch: Function) => {
    ScreenController.getInstance().onKeyBack();
    dispatch(setSelectedHelpOptionAction());
    dispatch(showMainSettingsScreen());
};

export const onKeyDownHelp = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.HELP]: { position },
        },
        help: { optionsList },
    } = getState();
    if (position < optionsList.length - 1) {
        dispatch(setSelectedHelpOptionAction(optionsList[position + 1]));
        dispatch(onKeyDownList(ListTypes.HELP));
    }
};

export const onKeyGuideHelp = () => (dispatch: Function) => {
    dispatch(setSelectedHelpOptionAction());
    dispatch(showGuideScreen());
};

export const onKeyOkHelp = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.HELP]: { position },
        },
        help: { optionsList },
    } = getState();
    const selectedItem = optionsList[position].value;
    switch (selectedItem) {
        case Locale.getString(LocaleKeys.SETTINGS_PC_PARENTAL_CONTROLS_TEXT):
            dispatch(showPCHelpScreen());
            break;

        case Locale.getString(LocaleKeys.CLOUD_DVR_SERVICE_TITLE):
            dispatch(showCloudDvrHelpScreen());
            break;
        default:
    }
};

export const onKeyUpHelp = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.HELP]: { position },
        },
        help: { optionsList },
    } = getState();
    if (position > 0) {
        dispatch(setSelectedHelpOptionAction(optionsList[position - 1]));
        dispatch(onKeyUpList(ListTypes.HELP));
    }
};

export const onMouseClickHelp = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    if (value.includes(ListTypes.HELP)) dispatch(onKeyOkHelp());
};

export const onMouseOutHelp = () => (dispatch: Function) => {
    dispatch(onMouseOutList());
};

export const onMouseOverHelp = () => (dispatch: Function, getState: Function) => {
    dispatch(onMouseOverList());
    const {
        list: {
            [ListTypes.HELP]: { position },
        },
        help: { optionsList },
    } = getState();
    dispatch(setSelectedHelpOptionAction(optionsList[position]));
};

export default helpReducer;
